// src/components/GytisManualExecuteCard.jsx
import React, { useState } from "react";
import axios from "axios";

function normalizeSymbolToSlash(s) {
  if (!s) return "";
  const t = s.trim().toUpperCase();
  if (t.includes("/")) return t;
  if (t.includes("-")) return t.replace("-", "/");
  // assume *USDT* style, e.g., BTCUSDT -> BTC/USDT
  if (t.endsWith("USDT")) return `${t.replace("USDT","")}/USDT`;
  return t;
}

export default function ManualExecuteCard({ apiBase }) {
  const [symbol, setSymbol] = useState("BTC/USDT");
  const [side, setSide] = useState("short"); // long | short
  const [entry, setEntry] = useState("");
  const [tp, setTp]       = useState("");
  const [sl, setSl]       = useState("");
  const [qtyUsd, setQty]  = useState("");    // optional override
  const [busy, setBusy]   = useState(false);
  const [resp, setResp]   = useState(null);
  const [err, setErr]     = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ title: "", lines: [], ok: false });
  // ...
  const onExec = async (e) => {
    e.preventDefault();
    setErr(""); setResp(null);

    const entryN = toDecimal(entry);
    const tpN    = toDecimal(tp);
    const slN    = toDecimal(sl);
    const qtyN   = qtyUsd ? toDecimal(qtyUsd) : null;

    if (![entryN, tpN, slN].every(Number.isFinite)) {
    setErr("Please enter valid numbers for entry / tp / sl.");
    return;
    }

    const token = normalizeSymbolToSlash(symbol);
    const isLong = side === "long";

    const suggestion = {
      token,
      analysis: {
        recommendation: isLong ? "BUY" : "SELL",
        score: 999,
        trendPhase: "manual",
        expectedDirection24h: "manual",
        longPosition:  isLong  ? { entry: entryN, tp: tpN, sl: slN, riskReward: 0 } : {},
        shortPosition: !isLong ? { entry: entryN, tp: tpN, sl: slN, riskReward: 0 } : {},
      }
    };
    
        const body = {
        suggestions: [{
            ...suggestion,
            ...(qtyN ? { qtyUsd: qtyN } : {})
        }]
        };
        
    setBusy(true);
    try {
      // Note: qtyUsd is optional — server uses its own if not provided
      const url = `${apiBase}/api/gytis/manual/execute`;
      const { data } = await axios.post(url, body, { timeout: 20000 });
      setResp(data);
      openOutcomeModal(data); 
    } catch (e2) {
      // Show HTTP-level error (backend 4xx/5xx)
      const httpMsg = e2?.response?.data?.error || e2?.response?.data || e2.message;
      setErr(typeof httpMsg === 'string' ? httpMsg : JSON.stringify(httpMsg));
      setModalData({
        title: "Request Failed",
        lines: [String(httpMsg || "Unknown error")],
        ok: false
      });
    } finally {
      setBusy(false);
    }
  };

 function humanizeStatus(s) {
   if (!s) return '';
   const map = {
     SKIPPED_NO_QTY_USD: 'Missing trade size (qtyUsd).',
     SKIPPED_PLAN_INVALID: 'Entry/TP/SL must be provided and numeric.',
     SKIPPED_GEOMETRY: 'TP/SL geometry invalid for the chosen side.',
     SKIPPED_GEOMETRY_AFTER_ADJ: 'After TP/SL adjustments, geometry became invalid.',
     SKIPPED_SAME_SIDE_OPEN: 'You already have an open position on this side.',
     SKIPPED_OPPOSITE_NOT_PROFIT: 'Opposite side open and not in profit; won’t flip.',
     SKIPPED_NOT_FLAT_TIMEOUT: 'Couldn’t flatten in time after closing opposite side.',
     SKIPPED_OUT_OF_TOLERANCE: 'Price deviates beyond entry tolerance.',
     SKIPPED_NO_REC: 'Recommendation must be BUY or SELL.',
     ERROR: 'Unexpected error while executing.',
     FILLED_MARKET: 'Market entry placed, TP/SL submitted.',
     TEST_MARKET: 'Test mode: simulated order.',
     CLOSE_SENT: 'Close order placed.',
     NO_POSITION: 'No position to close.'
   };
   return map[s] || s.replaceAll('_',' ').toLowerCase();
 }


 function Outcome() {
   const first = resp?.executed?.[0];
   if (!first) return null;
   const r = first.result || {};
   const status = r.status;
   const reason = r.reason;
   const ok = status === 'FILLED_MARKET' || status === 'TEST_MARKET' || status === 'CLOSE_SENT';
   return (
     <div style={{marginTop:12, padding:12, borderRadius:8,
                  border:'1px solid #1f2540',
                  background: ok ? '#0d1a13' : '#1a1010', color:'#cfe'}}>
       <div style={{display:'flex', alignItems:'center', gap:8}}>
         <span style={{
            display:'inline-block', width:10, height:10, borderRadius:'50%',
            background: ok ? '#2ecc71' : '#ff7373'
         }}/>
         <b>{ok ? 'Execution result' : 'Trade was not executed'}</b>
       </div>
       <div style={{marginTop:8}}>
         <div><b>Status:</b> {status} — {humanizeStatus(status)}</div>
         {reason ? <div><b>Reason:</b> {reason}</div> : null}
       </div>
       {first.original && (
         <div style={{marginTop:8, opacity:0.9}}>
           <b>Plan:</b> entry {first.original.entry}, tp {first.original.tp}, sl {first.original.sl}
         </div>
       )}
       {first.adjusted && (
         <div style={{opacity:0.9}}>
           <b>After Adjust:</b> entry {first.adjusted.entry}, tp {first.adjusted.tp}, sl {first.adjusted.sl}
         </div>
       )}
       {status === 'SKIPPED_OUT_OF_TOLERANCE' && r.deviationPct != null && (
         <div style={{marginTop:8}}>
           <b>Deviation:</b> {r.deviationPct.toFixed ? r.deviationPct.toFixed(3) : r.deviationPct}% (tolerance {r.tolPct}%)
         </div>
       )}
     </div>
   );
 }


function openOutcomeModal(apiResponse) {
   const first = apiResponse?.executed?.[0];
   if (!first) return;
   const r = first.result || {};
   const status = r.status;
   const ok = status === "FILLED_MARKET" || status === "TEST_MARKET" || status === "CLOSE_SENT";
   const lines = [];
   lines.push(`Status: ${status} — ${humanizeStatus(status)}`);
   if (r.reason) lines.push(`Reason: ${r.reason}`);
   if (typeof r.deviationPct === "number" && typeof r.tolPct === "number") {
     lines.push(`Deviation: ${r.deviationPct.toFixed(3)}% (tolerance ${r.tolPct}%)`);
   }
   if (first.original) {
     const o = first.original;
     lines.push(`Plan  → entry ${o.entry}, tp ${o.tp}, sl ${o.sl}`);
   }
   if (first.adjusted) {
     const a = first.adjusted;
     lines.push(`Adj.  → entry ${a.entry}, tp ${a.tp}, sl ${a.sl}`);
   }
   setModalData({
     title: ok ? "Trade Executed" : "Trade Skipped",
     lines,
     ok
   });
   setShowModal(true);
 }

  // helper: tolerant number parser
    function toDecimal(str) {
    if (str == null) return NaN;
    // remove spaces, turn comma decimals into dot, keep first numeric run
    const s = String(str).trim().replace(/\s+/g, "").replace(/,/g, ".");
    const m = s.match(/[+-]?\d*(?:\.\d+)?(?:e[+-]?\d+)?/i);
    const n = Number(m ? m[0] : NaN);
    return Number.isFinite(n) ? n : NaN;
    }

    // optional: keep UI value normalized on blur
    function normalizeForDisplay(v, set) {
    if (v == null || v === "") return;
    set(String(v).replace(/,/g, "."));
    }


      return (
    <div style={{
      background: "#0c0c14", borderRadius: 12, padding: 24, marginTop: 24,
      boxShadow: "0 0 0 1px #1f2540 inset"
    }}>
      <h2 style={{marginTop:0, color:"#fff"}}>Manual Execute</h2>
      <p style={{color:"#aab"}}>Sends a single suggestion to the executor. Uses the same tolerance, rounding, leverage, and TP/SL logic as the auto-bot.</p>

      <form onSubmit={onExec} style={{display:"grid", gap:12, gridTemplateColumns:"repeat(2, 1fr)"}}>
        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Symbol</span>
          <input
            value={symbol}
            onChange={e=>setSymbol(e.target.value)}
            placeholder="BTC/USDT or BTCUSDT"
            style={inputStyle}
          />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Side</span>
          <select value={side} onChange={e=>setSide(e.target.value)} style={inputStyle}>
            <option value="long">LONG (BUY)</option>
            <option value="short">SHORT (SELL)</option>
          </select>
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Entry</span>
          <input
            type="text"
            inputMode="decimal"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            onBlur={() => normalizeForDisplay(entry, setEntry)}
            placeholder="113200"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Take Profit</span>
         <input
            type="text"
            inputMode="decimal"
            value={tp}
            onChange={(e) => setTp(e.target.value)}
            onBlur={() => normalizeForDisplay(tp, setTp)}
            placeholder="111800"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>Stop Loss</span>
          <input
            type="text"
            inputMode="decimal"
            value={sl}
            onChange={(e) => setSl(e.target.value)}
            onBlur={() => normalizeForDisplay(sl, setSl)}
            placeholder="113900"
            style={inputStyle}
            />
        </label>

        <label style={{display:"grid", gap:6}}>
          <span style={{color:"#ccd"}}>USD Size (optional)</span>
          <input
            type="text"
            inputMode="decimal"
            value={qtyUsd}
            onChange={(e) => setQty(e.target.value)}
            onBlur={() => normalizeForDisplay(qtyUsd, setQty)}
            placeholder="leave blank to use server"
            style={inputStyle}
            />
        </label>

        <div style={{gridColumn:"1 / -1", display:"flex", gap:12}}>
          <button type="submit" disabled={busy} style={btnStyle}>
            {busy ? "Submitting…" : "Execute"}
          </button>
          <button type="button" onClick={() => { setResp(null); setErr(""); }} style={ghostBtnStyle}>
            Clear Output
          </button>
        </div>
      </form>

      {err && <div style={{marginTop:12, color:"#ff7373"}}>Error: {err}</div>}
     {resp && <Outcome />}
     {resp && (
       <details style={{marginTop:8}}>
         <summary style={{cursor:'pointer'}}>Raw response</summary>
         <pre style={{ background:"#0a0f1a", color:"#cfe", padding:16, borderRadius:8, maxHeight:300, overflow:"auto", border:"1px solid #1f2540" }}>
 {JSON.stringify(resp, null, 2)}
         </pre>
       </details>
     )}

     {/* Outcome Modal (uses your existing modal CSS classes) */}
     {showModal && (
       <div className="info-modal" onClick={() => setShowModal(false)}>
         <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
           <h3>{modalData.title}</h3>
           <div className="risk-columns">
             <div className="risk-column">
               <div className="risk-description">
                 {modalData.lines.map((line, i) => (
                   <p key={i} className="risk-text">{line}</p>
                 ))}
               </div>
             </div>
           </div>
           <div style={{ display:'flex', gap:8, marginTop:12 }}>
             <button
               onClick={() => {
                 try {
                   navigator.clipboard.writeText(JSON.stringify(resp ?? modalData, null, 2));
                 } catch {}
               }}
               className="close-btn"
             >
               Copy details
             </button>
             <button onClick={() => setShowModal(false)} className="close-btn">Close</button>
           </div>
         </div>
       </div>
     )}
     
    </div>
  );
}

const inputStyle = {
  background:"#0a0f1a", border:"1px solid #1f2540", color:"#e9edf7",
  borderRadius:10, padding:"10px 12px", outline:"none"
};
const btnStyle = {
  background:"#2ecc71", color:"#0a0f1a", border:"none",
  borderRadius:10, padding:"10px 16px", fontWeight:700, cursor:"pointer"
};
const ghostBtnStyle = {
  background:"transparent", color:"#cfe", border:"1px solid #1f2540",
  borderRadius:10, padding:"10px 16px", cursor:"pointer"
};
